import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as BackIcon } from '../../assets/images/icons/back.svg';
import Button from '../../components/Button';
import ActionContainer from '../../components/ActionContainer';
import USStateDropdown from '../../components/USStateDropdown';
import { fetchRecaptchaToken } from '../../lib/recaptcha';
import { ErrorMessages, RecaptchaError, UserFacingError, getCustomerMessageFromApolloError } from '../../lib/errors';
import formStyles from '../../styles/form.module.scss';
import styles from './EditShipping.module.scss';
import LocaleContext from '../../contexts/LocaleContext';

export interface AddressFields {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface EditShippingProps {
  onUpdateAddress: (addressFields: AddressFields, recaptchaToken: string) => void;
  onCancel: () => void;
  initialAddress?: AddressFields;
}

export default function EditShipping({ onUpdateAddress, onCancel, initialAddress }: EditShippingProps) {
  const { t } = useTranslation();
  const { country } = useContext(LocaleContext);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  // Form fields
  const [line1, setLine1] = useState(initialAddress?.line1 || '');
  const [line2, setLine2] = useState(initialAddress?.line2 || '');
  const [city, setCity] = useState(initialAddress?.city || '');
  const [state, setState] = useState(initialAddress?.state || '');
  const [zip, setZip] = useState(initialAddress?.zip || '');

  // Error fields
  const [line1Error, setLine1Error] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [stateError, setStateError] = useState(false);
  const [zipError, setZipError] = useState(false);

  const clearError = () => setError('');
  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (submitting) {
        return;
      }

      clearError();

      const validateForm = () => {
        const validations = [
          { value: line1, setError: setLine1Error },
          { value: city, setError: setCityError },
          { value: state, setError: setStateError },
          { value: zip, setError: setZipError },
        ];
        validations.forEach(({ setError: setFieldError }) => setFieldError(false));

        const isValid = validations.every(({ value, setError: setFieldError }) => {
          const isEmpty = !value.trim();
          if (isEmpty) setFieldError(true);
          return !isEmpty;
        });

        return isValid;
      };

      if (!validateForm()) {
        setError(t('please_fill_in_all_required_fields'));
        return;
      }

      setSubmitting(true);

      fetchRecaptchaToken('payment_add')
        .then((recaptchaToken) => {
          const addressFields: AddressFields = {
            line1: line1.trim(),
            line2: line2.trim() || undefined,
            city: city.trim(),
            state: state.trim(),
            zip: zip.trim(),
            country: country,
          };

          onUpdateAddress(addressFields, recaptchaToken);
        })
        .catch((err) => {
          const message =
            err instanceof UserFacingError
              ? err.message
              : err instanceof RecaptchaError
                ? t('we_were_unable_to_verify_your_information_please_reload_browser')
                : getCustomerMessageFromApolloError(err) || ErrorMessages.INPUT_ERROR;

          setError(message);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [submitting, line1, line2, city, state, zip, onUpdateAddress, country, t],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BackIcon onClick={onCancel} className={styles.backButton} />
        <h3>{t('edit_shipping')}</h3>
        <div className={styles.headerPlaceholder} />
      </div>

      <div className={styles.body}>
        <form className={formStyles.form} onSubmit={handleSubmit}>
          <div className={formStyles.formSection}>
            <input
              type="text"
              placeholder={t('address_placeholder')}
              value={line1}
              onChange={(e) => setLine1(e.target.value)}
              className={line1Error ? formStyles.error : ''}
              autoComplete="address-line1"
              data-hj-whitelist
            />

            <input
              type="text"
              placeholder={t('apt_suite_other_optional')}
              value={line2}
              onChange={(e) => setLine2(e.target.value)}
              autoComplete="address-line2"
              data-hj-whitelist
            />

            <input
              type="text"
              placeholder={t('city_placeholder')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={cityError ? formStyles.error : ''}
              autoComplete="address-level2"
              data-hj-whitelist
            />

            <div className={formStyles.formRow}>
              <USStateDropdown state={state} onChange={setState} error={stateError} />
            </div>
            <div className={formStyles.formRow}>
              <input
                type="text"
                placeholder={t('zip_code_placeholder')}
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className={zipError ? formStyles.error : ''}
                autoComplete="postal-code"
                data-hj-whitelist
              />
            </div>
          </div>

          <div className={styles.formActionContainer}>
            <ActionContainer>
              <Button disabled={submitting} loading={submitting} type="submit" className={styles.submitButton}>
                {t('update')}
              </Button>
            </ActionContainer>
          </div>
        </form>

        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  );
}
